import React from 'react';
import { Skeleton } from '@mui/material';
import { MuiPaper } from '@ace/ace-ui-web';
import styles from './SkeletonLocalLiftMax.module.css';

/**
 * SkeletonLocalLiftMax Component - Displays loading skeleton for Local Lift Max page
 * Mimics the structure of the Local Lift Max dashboard while data loads
 */
const SkeletonLocalLiftMax: React.FC = () => {
  return (
    <div className={styles.container}>
      <MuiPaper
        elevation={2}
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          margin: '0 auto',
          paddingRight: '50px',
          padding: '24px',
          marginBottom: '24px',
        }}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Skeleton
              variant="text"
              width={150}
              height={32}
              sx={{ marginBottom: '8px' }}
            />
            <Skeleton
              variant="text"
              width={180}
              height={20}
            />
          </div>
          <Skeleton
            variant="rectangular"
            width={220}
            height={40}
            sx={{ borderRadius: '4px' }}
          />
        </div>

        {/* Two-Column Grid Section */}
        <div className={styles.gridContainer}>
          {/* Latest Monthly Section */}
          <div className={styles.gridSection}>
            <div className={styles.sectionHeader}>
              <Skeleton
                variant="text"
                width={200}
                height={24}
              />
            </div>

            <div className={styles.table}>
              {/* Table Header */}
              <div className={styles.tableHeader}>
                <div className={styles.periodColumn}>
                  <Skeleton variant="text" width={60} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={90} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={100} height={20} />
                </div>
              </div>

              {/* Table Rows */}
              {[...Array(3)].map((_, index) => (
                <div key={`monthly-row-${index}`} className={styles.tableRow}>
                  <div className={styles.periodColumn}>
                    <Skeleton variant="text" width={120} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={80} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={90} height={18} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Year-to-Date Section */}
          <div className={styles.gridSection}>
            <div className={styles.sectionHeader}>
              <Skeleton
                variant="text"
                width={220}
                height={24}
              />
            </div>

            <div className={styles.table}>
              {/* Table Header */}
              <div className={styles.tableHeader}>
                <div className={styles.periodColumn}>
                  <Skeleton variant="text" width={60} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={90} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={100} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={80} height={20} />
                </div>
                <div className={styles.dataColumn}>
                  <Skeleton variant="text" width={140} height={20} />
                </div>
              </div>

              {/* Table Rows */}
              {[...Array(3)].map((_, index) => (
                <div key={`ytd-row-${index}`} className={styles.tableRow}>
                  <div className={styles.periodColumn}>
                    <Skeleton variant="text" width={120} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={80} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={90} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={85} height={18} />
                  </div>
                  <div className={styles.dataColumn}>
                    <Skeleton variant="text" width={70} height={18} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className={styles.investmentSection}>
          <Skeleton
            variant="text"
            width={280}
            height={20}
          />
        </div>

        {/* Contact Link */}
        <div className={styles.contactSection}>
          <Skeleton
            variant="text"
            width={240}
            height={18}
          />
        </div>
      </MuiPaper>

      {/* ROAS Requirements Section */}
      <MuiPaper
        elevation={2}
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          margin: '0 auto',
          paddingRight: '50px',
          padding: '24px',
        }}>
        <div className={styles.roasHeader}>
          <Skeleton
            variant="text"
            width={350}
            height={24}
          />
        </div>

        <div className={styles.roasTable}>
          {/* Table Header */}
          <div className={styles.roasTableHeader}>
            <div className={styles.roasLabelColumn}>
              <Skeleton variant="text" width={100} height={20} />
            </div>
            <div className={styles.roasValueColumn}>
              <Skeleton variant="text" width={60} height={20} />
            </div>
          </div>

          {/* Table Rows */}
          {[...Array(3)].map((_, index) => (
            <div key={`roas-row-${index}`} className={styles.roasTableRow}>
              <div className={styles.roasLabelColumn}>
                <Skeleton variant="text" width={180} height={18} />
              </div>
              <div className={styles.roasValueColumn}>
                <Skeleton variant="text" width={50} height={18} />
              </div>
            </div>
          ))}
        </div>
      </MuiPaper>
    </div>
  );
};

export default SkeletonLocalLiftMax;
